import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../siteConfig.js";

const PlayIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.18 23.76c.3.17.64.24.99.19L14.9 12 3.18.05A1.5 1.5 0 0 0 3 .72v22.56c0 .19.06.36.18.48z"/>
    <path d="M19.35 9.29 16.3 7.54 12.83 12l3.47 4.46 3.05-1.75A2 2 0 0 0 20.5 12a2 2 0 0 0-1.15-2.71z"/>
    <path d="M4.17.05 15.6 11.3 4.17 23.95l11-6.3L20.5 12 4.17.05z" opacity=".5"/>
  </svg>
);

export default function Home() {
  return (
    <div>
      {/* ── HERO ── */}
      <div className="container">
        <div className="heroSection">
          <div className="heroLeft">
            <div className="heroBadge">
              <span className="heroBadgeDot" />
              Now live on Google Play
            </div>
            <h1 className="heroTitle">
              Your neighbourhood,<br />
              <span>delivered.</span>
            </h1>
            <p className="heroSubtitle">
              Rahisi connects you to local retailers — browse products, unlock deals,
              and get same-day delivery across Nairobi. All from one app.
            </p>
            <div className="heroCtas">
              <a
                className="playStoreBtn"
                href={siteConfig.playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <PlayIcon size={22} />
                <div className="playStoreBtnInner">
                  <span className="playStoreBtnSmall">GET IT ON</span>
                  <span className="playStoreBtnLabel">Google Play</span>
                </div>
              </a>
            </div>
            <div className="heroStats">
              <div className="heroStat">
                <div className="statNum">1–3h</div>
                <div className="statLabel">Nairobi delivery</div>
              </div>
              <div className="heroStat">
                <div className="statNum">M-Pesa</div>
                <div className="statLabel">Payments accepted</div>
              </div>
              <div className="heroStat">
                <div className="statNum">Free</div>
                <div className="statLabel">Always free to download</div>
              </div>
            </div>
          </div>
          <div className="heroRight">
            <img src="/tablet_bg_1.png" alt="Rahisi app on mobile" />
            <div className="heroRightOverlay" />
            <div className="heroRightTag">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="rgba(191,92,0,0.3)"/>
                <path d="M8 12l3 3 5-5" stroke="#FFB84D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>
                <div className="heroTagText">Live order tracking</div>
                <div className="heroTagSub">Know exactly where your order is</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div className="container section">
        <div className="sectionHeader">
          <span className="eyebrow">Simple by design</span>
          <h2 className="h2">Order in three steps</h2>
          <p className="lead">From browsing to your door — Rahisi is built for speed and simplicity.</p>
        </div>
        <div className="stepsGrid">
          <div className="step">
            <div className="stepNum">1</div>
            <div className="stepTitle">Browse & discover</div>
            <p className="stepDesc">Explore products from local Nairobi retailers, sorted by category, deals, and what's trending near you.</p>
          </div>
          <div className="step">
            <div className="stepNum">2</div>
            <div className="stepTitle">Add to cart & pay</div>
            <p className="stepDesc">Pay securely with M-Pesa STK push or card — no manual input, no waiting, instant confirmation.</p>
          </div>
          <div className="step">
            <div className="stepNum">3</div>
            <div className="stepTitle">Track your delivery</div>
            <p className="stepDesc">Watch your rider move toward you on a live map. Get notified at every stage, from packed to delivered.</p>
          </div>
        </div>
      </div>

      {/* ── FEATURES ── */}
      <div className="container section">
        <div className="sectionHeader">
          <span className="eyebrow">Built for everyday shopping</span>
          <h2 className="h2">Everything you need, nothing you don't</h2>
        </div>
        <div className="featuresGrid">
          {[
            { icon: "🛍️", title: "Product discovery", desc: "Browse hundreds of products across categories — groceries, electronics, fashion, and more from verified local sellers." },
            { icon: "🏷️", title: "Flash deals & promos", desc: "Time-limited deals and promo codes updated daily. Never miss a discount from sellers near you." },
            { icon: "📍", title: "Same-day delivery", desc: "Orders placed before 4 PM ship same day within Nairobi. Outside Nairobi, receive in 1–3 business days." },
            { icon: "📱", title: "M-Pesa payments", desc: "Pay with M-Pesa STK push in seconds. No typing. No waiting. Instant order confirmation on your phone." },
            { icon: "🗺️", title: "Live rider tracking", desc: "A real-time map shows your rider's location. Know your ETA to the minute, every time." },
            { icon: "🔁", title: "Easy returns", desc: "7-day hassle-free return window for unused items. We make it straightforward from the app." },
          ].map((f) => (
            <div className="featureCard" key={f.title}>
              <div className="featureCardIcon">{f.icon}</div>
              <div className="featureCardTitle">{f.title}</div>
              <p className="featureCardDesc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── WHO IS RAHISI FOR ── */}
      <div className="container section">
        <div className="sectionHeader">
          <span className="eyebrow">Who it's for</span>
          <h2 className="h2">One platform, three communities</h2>
          <p className="lead">Rahisi serves shoppers, delivery riders, and the merchants who make it all possible.</p>
        </div>
        <div className="audienceGrid">
          <div className="audienceCard">
            <img src="/tablet_bg_2.png" alt="Shoppers" />
            <div className="audienceOverlay" />
            <div className="audienceBody">
              <span className="audienceTag">Shoppers</span>
              <div className="audienceTitle">Shop from your neighbourhood</div>
              <p className="audienceDesc">Discover products, grab deals, and get fast delivery — all from the Rahisi app on your Android phone.</p>
              <a href={siteConfig.playStoreUrl} target="_blank" rel="noopener noreferrer" className="audienceLink">
                Download free →
              </a>
            </div>
          </div>
          <div className="audienceCard">
            <img src="/tablet_bg_3.png" alt="Riders" />
            <div className="audienceOverlay" />
            <div className="audienceBody">
              <span className="audienceTag">Riders</span>
              <div className="audienceTitle">Earn on your schedule</div>
              <p className="audienceDesc">Join as a delivery partner. Accept orders, navigate with live maps, and track your earnings in real time.</p>
              <Link to="/contact" className="audienceLink">
                Become a rider →
              </Link>
            </div>
          </div>
          <div className="audienceCard">
            <img src="/tablet_bg_4.png" alt="Merchants" />
            <div className="audienceOverlay" />
            <div className="audienceBody">
              <span className="audienceTag">Merchants</span>
              <div className="audienceTitle">Grow your retail business</div>
              <p className="audienceDesc">List your products, manage inventory, run flash deals, and reach thousands of customers across Nairobi.</p>
              <Link to="/contact" className="audienceLink">
                Partner with us →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── SCREENSHOTS ── */}
      <div className="container section">
        <div className="sectionHeader">
          <span className="eyebrow">Inside the app</span>
          <h2 className="h2">A retail experience built for Kenya</h2>
        </div>
        <div className="screenshotsGrid">
          {[
            { img: "/tablet_bg_5.png", label: "Home feed" },
            { img: "/tablet_bg_6.png", label: "Product details" },
            { img: "/tablet_bg_7.png", label: "Flash deals" },
            { img: "/tablet_bg_8.png", label: "Order tracking" },
          ].map((s) => (
            <div className="screenshotCard" key={s.label}>
              <img src={s.img} alt={s.label} />
              <div className="screenshotLabel">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TRUST ── */}
      <div className="container">
        <div className="trustStrip">
          {[
            { icon: "🔒", text: "Secure M-Pesa payments" },
            { icon: "📦", text: "Same-day Nairobi delivery" },
            { icon: "↩️", text: "7-day easy returns" },
            { icon: "🗺️", text: "Live rider tracking" },
            { icon: "🇰🇪", text: "Built for Kenya" },
          ].map((t) => (
            <div className="trustItem" key={t.text}>
              <span className="trustIcon">{t.icon}</span>
              <span className="trustText">{t.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="container section">
        <div className="sectionHeader">
          <span className="eyebrow">Common questions</span>
          <h2 className="h2">Got questions? We've got answers.</h2>
        </div>
        <div className="faqGrid">
          {[
            { q: "Is Rahisi free to download?", a: "Yes — Rahisi is completely free to download and browse. You only pay for what you order." },
            { q: "How fast is delivery in Nairobi?", a: "Orders placed before 4 PM on in-stock items are delivered same day within Nairobi, usually within 1–3 hours." },
            { q: "How do I pay?", a: "We support M-Pesa STK push (Lipa na M-Pesa), which triggers a payment prompt directly on your phone. Card payments are also accepted." },
            { q: "Can I track my order?", a: "Yes. Once your order is picked up by a rider, you see their live location on a map and receive status updates at every stage." },
            { q: "What if I want to return an item?", a: "You have a 7-day return window from delivery. Items must be unused and in original condition. Initiate through the Orders section in the app." },
            { q: "Which areas do you deliver to?", a: "We primarily serve Nairobi for same-day delivery. Orders outside Nairobi are delivered within 1–3 business days depending on location." },
          ].map((faq) => (
            <div className="faqItem" key={faq.q}>
              <div className="faqQ">{faq.q}</div>
              <p className="faqA">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="container">
        <div className="ctaSection">
          <div className="ctaSectionText">
            <h2 className="ctaTitle">Start shopping smarter today.</h2>
            <p className="ctaSubtitle">
              Download Rahisi free on Android and get same-day delivery from local retailers near you.
            </p>
          </div>
          <div className="ctaActions">
            <a
              className="playStoreBtn"
              href={siteConfig.playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PlayIcon size={22} />
              <div className="playStoreBtnInner">
                <span className="playStoreBtnSmall">GET IT ON</span>
                <span className="playStoreBtnLabel">Google Play</span>
              </div>
            </a>
            <span className="ctaNote">Free · Android · Available now in Kenya</span>
          </div>
        </div>
      </div>

    </div>
  );
}
